import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import {
  useBuildLetterTemplate, useBuildTermTemplate, useHiringBoard, useIssueLetter, useRaiseRequisition,
  useRequisitionCandidates,
} from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

/** What happens after somebody says yes (HRX-10).
 *
 * The front of recruitment on this site is well built — 21 postings, 22 screened candidates, 41 interview
 * questions, 9 channels. Everything after the offer was missing: no offer-term library, no appointment letter
 * for anybody hired, no feedback behind any interview decision, and no referral scheme.
 *
 * Two of those gaps are closed by a template and the third is not closed at all. Interview decisions already
 * taken stay undocumented on purpose — inventing a reason after the fact is the one thing that would make the
 * record actively misleading, and the count of decisions nobody can account for is what the screen is for. */
export default function Paperwork({ company }: { company?: string }) {
  const { t } = useTranslation();
  const q = useHiringBoard(company);
  const terms = useBuildTermTemplate();
  const letters = useBuildLetterTemplate();
  const issue = useIssueLetter();
  const reqs = useRequisitionCandidates(company);
  const raise = useRaiseRequisition();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-hiring-paperwork>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-hiring-strip>
        <Stat label={t("hire.offers")} value={d.offers.length}
          hint={t("hire.offersHint", { stale: d.stale_offers.length })} />
        <Stat label={t("hire.repetition")} value={`${d.library.repetition_pct} %`}
          cls={d.templates.terms ? "text-status-green" : "text-status-amber"}
          hint={t("hire.repetitionHint", { n: d.library.terms_written })} />
        <Stat label={t("hire.letters")} value={`${d.letters.with_letter}/${d.letters.hires}`}
          cls={d.letters.without_letter ? "text-status-amber" : "text-status-green"}
          hint={t("hire.lettersHint")} />
        <Stat label={t("hire.undocumented")} value={d.interviews.undocumented}
          cls={d.interviews.undocumented ? "text-status-red" : "text-status-green"}
          hint={t("hire.undocumentedHint", { n: d.interviews.decided })} />
        <Stat label={t("hire.referrals")} value={d.referrals.referrals}
          hint={t("hire.referralsHint", { due: d.referrals.bonus_due })} />
      </div>

      {failing.length > 0 && (
        <SectionCard title={t("hire.findings")} aside={t("hire.findingsN", { n: failing.length, of: d.state.length })}>
          <ul>
            {failing.map((s) => (
              <li key={s.key} className="flex flex-wrap items-start justify-between gap-3 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
                data-hiring-finding={s.key}>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
                  <div>
                    <div className="font-semibold">{t(`hire.check.${s.key}.title`)}</div>
                    <p className="mt-0.5 max-w-3xl text-sm text-brand-grey">{t(`hire.check.${s.key}.bad`, s.facts) as string}</p>
                  </div>
                </div>
                {d.permissions.manage && s.key === "term_library" && (
                  <button onClick={() => terms.mutate()} disabled={terms.isPending} data-hiring-build-terms
                    className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {t("hire.buildTerms")}
                  </button>
                )}
                {d.permissions.manage && s.key === "letters" && !d.templates.letters && (
                  <button onClick={() => letters.mutate()} disabled={letters.isPending} data-hiring-build-letters
                    className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {t("hire.buildLetters")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title={t("hire.offerList")} aside={t("hire.offerListN", { n: d.offers.length })}>
          <table className="w-full text-sm">
            <tbody>
              {d.offers.map((o) => (
                <tr key={o.name} className="border-t border-brand-grey-light first:border-t-0" data-hiring-offer={o.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{o.applicant_name}</div>
                    <div className="text-[11px] text-brand-grey">{o.designation} · {o.company} · {o.offer_date}</div>
                    {o.coverage.missing.length > 0 && (
                      <div className="text-[11px] text-status-amber" data-hiring-offer-missing={o.name}>
                        {t("hire.missingTerms", { list: o.coverage.missing.join(", ") })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                      o.status === "Accepted" ? "bg-green-50 text-status-green"
                        : o.state.stale ? "bg-amber-50 text-status-amber" : "bg-brand-grey-light text-brand-grey"}`}>
                      {o.status}
                    </span>
                    {o.state.stale && <div className="text-[11px] text-status-amber">{t("hire.staleDays", { n: o.state.days })}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>

        <SectionCard title={t("hire.letterQueue")} aside={t("hire.letterQueueN", { n: d.letters.without_letter })}>
          {d.letters.without_letter === 0 ? (
            <div className="p-6 text-center text-sm text-brand-grey" data-hiring-letters-clear>{t("hire.allLettered")}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {d.letters.missing.map((h) => (
                  <tr key={h.name ?? ""} className="border-t border-brand-grey-light first:border-t-0"
                    data-hiring-letter-missing={h.job_applicant ?? ""}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{h.employee_name}</div>
                      <div className="text-[11px] text-brand-grey">{h.designation} · {t("hire.joined", { date: h.date_of_joining ?? "" })}</div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {d.permissions.manage && d.templates.letters ? (
                        <button onClick={() => issue.mutate({ job_applicant: h.job_applicant ?? "" })}
                          disabled={issue.isPending} data-hiring-issue={h.job_applicant ?? ""}
                          className="rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                          {t("hire.issue")}
                        </button>
                      ) : <span className="text-[11px] text-brand-grey">{t("hire.needsTemplate")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      <SectionCard title={t("hire.referralScheme")} aside={t("hire.referralsN", { n: d.referrals.referrals, pct: d.referrals.conversion_pct })}>
        {d.referral_rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-hiring-referrals-empty>{t("hire.noReferrals")}</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {d.referral_rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light first:border-t-0" data-hiring-referral={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.full_name}</div>
                    <div className="text-[11px] text-brand-grey">
                      {t("hire.referredBy", { who: r.referrer_name ?? "", role: r.for_designation ?? "" })}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                      r.status === "Accepted" ? "bg-green-50 text-status-green" : "bg-brand-grey-light text-brand-grey"}`}>
                      {r.status}
                    </span>
                    {r.status === "Accepted" && r.is_applicable_for_referral_bonus && r.referral_payment_status !== "Paid" && (
                      <div className="text-[11px] text-status-amber" data-hiring-bonus-due={r.name}>{t("hire.bonusDue")}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={t("rq.title")}
        aside={reqs.data ? t("rq.n", { n: reqs.data.without_a_request, of: reqs.data.count }) : undefined}>
        <p className="border-b border-brand-grey-light px-4 py-2 text-[12px] text-brand-grey">{t("rq.hint")}</p>
        {!reqs.data ? (
          <div className="p-4"><Skeleton className="h-16" /></div>
        ) : reqs.data.without_a_request === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-rec-req-empty>{t("rq.allHave")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("rq.opening")}</th>
                  <th className="px-4 py-2">{t("rq.designation")}</th>
                  <th className="px-2 py-2 text-right">{t("rq.positions")}</th>
                  <th className="px-2 py-2 text-right">{t("rq.compensation")}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {reqs.data.rows.filter((r) => !r.requisition).map((r) => (
                  <tr key={r.opening} className="border-t border-brand-grey-light" data-rec-req={r.opening}>
                    <td className="px-4 py-2">
                      <div className="truncate font-medium">{r.job_title}</div>
                      <div className="text-[11px] text-brand-grey">{r.opening} · {r.company} · {r.status} · {r.posted_on}</div>
                    </td>
                    <td className="px-4 py-2 text-[12px]">{r.designation}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.fields.no_of_positions ?? "–"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {r.fields.expected_compensation
                        ? <>{r.fields.expected_compensation.toLocaleString()} <span className="text-[11px] text-brand-grey">{r.currency} · {t(`rq.basis.${r.fields.compensation_basis}`)}</span></>
                        : <span className="text-[11px] text-status-amber">{r.reason}</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {reqs.data.can_raise && r.ok && (
                        <button data-rec-req-raise={r.opening} disabled={raise.isPending}
                          onClick={() => raise.mutate({ opening: r.opening })}
                          className="rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                          {t("rq.raise")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {(terms.isError || letters.isError || issue.isError || raise.isError) && (
        <ErrorBox error={terms.error ?? letters.error ?? issue.error ?? raise.error} />
      )}
    </div>
  );
}
